import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Users, Shield, Check, Save, AlertCircle } from 'lucide-react';

const TournamentRosterModal = ({ isOpen, onClose, team, tournament, currentRoster = [], onSave }) => {
    const [selectedPlayerIds, setSelectedPlayerIds] = useState(new Set(currentRoster));
    const [searchQuery, setSearchQuery] = useState('');
    const [saving, setSaving] = useState(false);

    // Reset selection when modal opens or team/roster changes
    useEffect(() => {
        if (isOpen) {
            setSelectedPlayerIds(new Set(currentRoster));
            setSearchQuery('');
        }
    }, [isOpen, currentRoster, team]);

    // Filter players based on search
    const filteredPlayers = useMemo(() => {
        if (!team?.players) return [];

        return team.players.filter(player => {
            const name = player.name || 'Unknown';
            const email = player.email || '';
            const query = searchQuery.toLowerCase();
            return name.toLowerCase().includes(query) || email.toLowerCase().includes(query);
        }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [team, searchQuery]);

    // Statistics
    const selectedCount = selectedPlayerIds.size;
    const maxSquadSize = tournament?.maxRosterSize || 0;
    const isOverLimit = maxSquadSize > 0 && selectedCount > maxSquadSize;

    const togglePlayer = (playerId) => {
        const newSelected = new Set(selectedPlayerIds);
        if (newSelected.has(playerId)) {
            newSelected.delete(playerId);
        } else {
            newSelected.add(playerId);
        }
        setSelectedPlayerIds(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedCount === filteredPlayers.length) {
            // If all filtered are selected, deselect them
            const newSelected = new Set(selectedPlayerIds);
            filteredPlayers.forEach(p => newSelected.delete(p.id || p.userId));
            setSelectedPlayerIds(newSelected);
        } else {
            // Select all filtered
            const newSelected = new Set(selectedPlayerIds);
            filteredPlayers.forEach(p => newSelected.add(p.id || p.userId));
            setSelectedPlayerIds(newSelected);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(Array.from(selectedPlayerIds));
            onClose();
        } catch (error) {
            console.error('Error saving roster:', error);
            alert('Failed to save roster. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="p-6 border-b flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <Users className="text-blue-600" />
                            Manage Tournament Roster
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Select players from <span className="font-semibold text-gray-700">{team?.name}</span> for <span className="font-semibold text-gray-700">{tournament?.name}</span>
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500">
                        <X size={24} />
                    </button>
                </div>

                {/* Stats Bar */}
                <div className="px-6 py-3 bg-blue-50 border-b flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                        <span className="text-gray-600">
                            Total Team Size: <span className="font-bold">{team?.players?.length || 0}</span>
                        </span>
                        <span className={`flex items-center gap-1 ${isOverLimit ? 'text-red-600 font-bold' : 'text-blue-600 font-semibold'}`}>
                            Selected: {selectedCount}
                            {maxSquadSize > 0 && <span> / {maxSquadSize} Max</span>}
                        </span>
                    </div>
                    {isOverLimit && (
                        <div className="flex items-center gap-1 text-red-600 text-xs font-bold">
                            <AlertCircle size={14} />
                            Over Limit
                        </div>
                    )}
                </div>

                {/* Filters */}
                <div className="p-4 border-b space-y-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search players by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    <div className="flex justify-between items-center text-sm">
                        <button
                            onClick={handleSelectAll}
                            className="text-blue-600 font-medium hover:text-blue-700 hover:underline"
                        >
                            {selectedCount === filteredPlayers.length && filteredPlayers.length > 0 ? "Deselect All Visible" : "Select All Visible"}
                        </button>
                        <span className="text-gray-500">
                            Showing {filteredPlayers.length} players
                        </span>
                    </div>
                </div>

                {/* Player List */}
                <div className="flex-1 overflow-y-auto p-2">
                    {filteredPlayers.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <Users size={48} className="mx-auto mb-3 text-gray-300" />
                            <p>No players found matching "{searchQuery}"</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-2">
                            {filteredPlayers.map(player => {
                                const playerId = player.id || player.userId;
                                const isSelected = selectedPlayerIds.has(playerId);

                                return (
                                    <div
                                        key={playerId}
                                        onClick={() => togglePlayer(playerId)}
                                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${isSelected
                                                ? 'bg-blue-50 border-blue-200 shadow-sm'
                                                : 'bg-white border-gray-100 hover:bg-gray-50'
                                            }`}
                                    >
                                        <div className={`w-5 h-5 rounded border flex items-center justify-center mr-4 transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                                            }`}>
                                            {isSelected && <Check size={14} className="text-white" />}
                                        </div>

                                        <div className="flex-1">
                                            <div className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-800'}`}>
                                                {player.name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {player.role || 'Player'}
                                            </div>
                                        </div>

                                        {/* Status Badge (Optional - can add more details here later) */}
                                        <div className="text-xs text-gray-400">
                                            {/* {player.status || 'Active'} */}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t bg-gray-50 flex justify-end gap-3 rounded-b-xl">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-white transition-colors"
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving || isOverLimit}
                        className={`px-5 py-2.5 rounded-lg text-white font-medium flex items-center gap-2 shadow-sm ${saving || isOverLimit
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                            } transition-all`}
                    >
                        {saving ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-b-transparent"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save size={18} />
                                Save Roster ({selectedCount})
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TournamentRosterModal;
